import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, TrendingUp, Clock, Eye, ArrowRight, Leaf, UserCog, FileDown, FileText, RefreshCw, FolderOpen, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { statsAPI, ordersAPI } from '../lib/api';
import { formatPrice, formatDateTime, ORDER_STATUS } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const QUOTE_STATUS_MINI = {
    pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
    responded: { label: 'Repondu', color: 'bg-blue-100 text-blue-800' },
    converted: { label: 'Converti', color: 'bg-emerald-100 text-emerald-800' },
    cancelled: { label: 'Annule', color: 'bg-red-100 text-red-800' },
};

export const ClientDashboard = () => {
    const { user } = useAuth();
    const { addItem } = useCart();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadStats = async () => {
            try {
                const res = await statsAPI.getClientStats();
                setStats(res.data);
            } catch (error) {
                console.error('Error loading stats:', error);
            } finally {
                setLoading(false);
            }
        };
        loadStats();
    }, []);

    const chartData = stats?.monthly_stats?.map(m => ({
        month: m._id,
        commandes: m.count,
        total: m.total,
    })).reverse() || [];

    const handleReorder = async (order) => {
        if (!order.items || order.items.length === 0) return;
        let count = 0;
        for (const item of order.items) {
            addItem({
                id: item.product_id,
                name: item.product_name,
                price: item.price || 0,
                unit: item.unit || 'kg',
                image_url: item.image_url || '',
                min_order: 1,
                ready_to_order: true,
            }, item.quantity || 1);
            count++;
        }
        toast.success(`${count} produit(s) ajoute(s) au panier commande`);
        navigate('/commander');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="client-dashboard">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-xl bg-leaf/10 flex items-center justify-center">
                            <Leaf className="w-6 h-6 text-leaf" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">
                                Bonjour, {user?.contact_name}
                            </h1>
                            <p className="text-muted-foreground">{user?.company_name}</p>
                        </div>
                        <Link to="/dashboard/profil" className="ml-auto">
                            <Button variant="outline" className="border-jungle text-jungle hover:bg-jungle/10" data-testid="edit-profile-btn">
                                <UserCog className="w-4 h-4 mr-2" />
                                Mon Profil
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                    <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                        <CardContent className="pt-4 pb-4 text-center">
                            <div className="w-10 h-10 rounded-xl bg-jungle/10 mx-auto mb-2 flex items-center justify-center">
                                <Package className="h-5 w-5 text-jungle" />
                            </div>
                            <div className="text-2xl font-bold text-jungle" data-testid="total-orders">{stats?.total_orders || 0}</div>
                            <p className="text-xs text-muted-foreground mt-1">Commandes</p>
                        </CardContent>
                    </Card>
                    <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                        <CardContent className="pt-4 pb-4 text-center">
                            <div className="w-10 h-10 rounded-xl bg-terracotta/10 mx-auto mb-2 flex items-center justify-center">
                                <Clock className="h-5 w-5 text-terracotta" />
                            </div>
                            <div className="text-2xl font-bold text-terracotta" data-testid="pending-orders">{stats?.pending_orders || 0}</div>
                            <p className="text-xs text-muted-foreground mt-1">En cours</p>
                        </CardContent>
                    </Card>
                    <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                        <CardContent className="pt-4 pb-4 text-center">
                            <div className="w-10 h-10 rounded-xl bg-leaf/10 mx-auto mb-2 flex items-center justify-center">
                                <TrendingUp className="h-5 w-5 text-leaf" />
                            </div>
                            <div className="text-2xl font-bold text-leaf" data-testid="total-spent">{formatPrice(stats?.total_spent || 0)}</div>
                            <p className="text-xs text-muted-foreground mt-1">Total depense</p>
                        </CardContent>
                    </Card>
                    <Card className="border-0 bg-amber-50/80 backdrop-blur-sm shadow-sm rounded-2xl">
                        <CardContent className="pt-4 pb-4 text-center">
                            <div className="w-10 h-10 rounded-xl bg-amber-100 mx-auto mb-2 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-amber-700" />
                            </div>
                            <div className="text-2xl font-bold text-amber-700" data-testid="total-quotes">{stats?.total_quotes || 0}</div>
                            <p className="text-xs text-muted-foreground mt-1">Demandes envoyees</p>
                        </CardContent>
                    </Card>
                    <Card className="border-0 bg-amber-50/80 backdrop-blur-sm shadow-sm rounded-2xl">
                        <CardContent className="pt-4 pb-4 text-center">
                            <div className="w-10 h-10 rounded-xl bg-amber-100 mx-auto mb-2 flex items-center justify-center">
                                <Clock className="h-5 w-5 text-amber-600" />
                            </div>
                            <div className="text-2xl font-bold text-amber-600" data-testid="pending-quotes">{stats?.pending_quotes || 0}</div>
                            <p className="text-xs text-muted-foreground mt-1">Demandes en attente</p>
                        </CardContent>
                    </Card>
                    <Card className="border-0 bg-jungle shadow-sm rounded-2xl">
                        <CardContent className="pt-4 pb-4 text-center flex flex-col items-center justify-center h-full">
                            <Button asChild variant="secondary" size="sm" className="w-full bg-leaf hover:bg-leaf/90 text-jungle rounded-xl" data-testid="new-order-button">
                                <Link to="/catalogue">
                                    <ShoppingCart className="h-4 w-4 mr-1" />
                                    Commander
                                </Link>
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Chart */}
                    <Card className="lg:col-span-2 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                        <CardHeader>
                            <CardTitle className="font-heading text-jungle">Historique des commandes</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(150 15% 85%)" />
                                        <XAxis dataKey="month" stroke="hsl(150 20% 40%)" fontSize={12} />
                                        <YAxis stroke="hsl(150 20% 40%)" fontSize={12} />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid hsl(150 15% 85%)', borderRadius: '12px' }}
                                            formatter={(value, name) => [name === 'total' ? formatPrice(value) : value, name === 'total' ? 'Total' : 'Commandes']}
                                        />
                                        <Bar dataKey="commandes" fill="hsl(142 60% 40%)" radius={[8, 8, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                                    <div className="text-center">
                                        <Leaf className="w-12 h-12 mx-auto mb-4 text-jungle/20" />
                                        <p>Pas encore de donnees</p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Quick Actions */}
                    <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                        <CardHeader>
                            <CardTitle className="font-heading text-jungle">Actions rapides</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Button asChild variant="outline" className="w-full justify-start border-jungle/20 hover:bg-jungle/5 rounded-xl">
                                <Link to="/catalogue">
                                    <ShoppingCart className="h-4 w-4 mr-2 text-leaf" />
                                    Parcourir le catalogue
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="w-full justify-start border-jungle/20 hover:bg-jungle/5 rounded-xl">
                                <Link to="/dashboard/commandes">
                                    <Package className="h-4 w-4 mr-2 text-leaf" />
                                    Mes commandes
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="w-full justify-start border-amber-200 hover:bg-amber-50 rounded-xl">
                                <Link to="/dashboard/devis">
                                    <FileText className="h-4 w-4 mr-2 text-amber-600" />
                                    Mes demandes de prix
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="w-full justify-start border-jungle/20 hover:bg-jungle/5 rounded-xl">
                                <Link to="/dashboard/catalogues">
                                    <FolderOpen className="h-4 w-4 mr-2 text-leaf" />
                                    Documents & Offres
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="w-full justify-start border-jungle/20 hover:bg-jungle/5 rounded-xl">
                                <Link to="/dashboard/bonnes-pratiques">
                                    <BookOpen className="h-4 w-4 mr-2 text-leaf" />
                                    Bonnes pratiques
                                </Link>
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Recent Orders with Reorder */}
                <Card className="mt-8 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="font-heading text-jungle">Commandes recentes</CardTitle>
                        <Button asChild variant="ghost" size="sm" className="text-leaf hover:text-leaf hover:bg-leaf/10">
                            <Link to="/dashboard/commandes">
                                Voir tout
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Link>
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {stats?.recent_orders?.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-jungle/10">
                                        <TableHead className="text-jungle/70">N° Commande</TableHead>
                                        <TableHead className="text-jungle/70">Date</TableHead>
                                        <TableHead className="text-jungle/70">Total</TableHead>
                                        <TableHead className="text-jungle/70">Statut</TableHead>
                                        <TableHead className="text-jungle/70 text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {stats.recent_orders.map((order) => (
                                        <TableRow key={order.id} className="border-jungle/10" data-testid={`order-row-${order.id}`}>
                                            <TableCell className="font-medium text-jungle">{order.order_number}</TableCell>
                                            <TableCell className="text-muted-foreground">{formatDateTime(order.created_at)}</TableCell>
                                            <TableCell className="font-semibold text-leaf">{formatPrice(order.total)}</TableCell>
                                            <TableCell>
                                                <Badge className={`${ORDER_STATUS[order.status]?.color} rounded-full`}>
                                                    {ORDER_STATUS[order.status]?.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex justify-end gap-1">
                                                    <Button asChild variant="ghost" size="sm" className="hover:bg-leaf/10">
                                                        <Link to={`/dashboard/commande/${order.id}`}>
                                                            <Eye className="h-4 w-4 text-leaf" />
                                                        </Link>
                                                    </Button>
                                                    <Button variant="ghost" size="sm" className="hover:bg-leaf/10" onClick={async () => {
                                                        try {
                                                            const res = await ordersAPI.downloadPdf(order.id);
                                                            const url = window.URL.createObjectURL(new Blob([res.data]));
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.download = `BonDeCommande_${order.order_number}.pdf`;
                                                            link.click();
                                                            window.URL.revokeObjectURL(url);
                                                        } catch (err) { console.error('Error downloading PDF:', err); }
                                                    }} data-testid={`client-pdf-${order.id}`}>
                                                        <FileDown className="h-4 w-4 text-leaf" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" className="hover:bg-emerald-50 text-emerald-600"
                                                        onClick={() => handleReorder(order)}
                                                        title="Recommander"
                                                        data-testid={`reorder-${order.id}`}>
                                                        <RefreshCw className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <Leaf className="w-12 h-12 mx-auto mb-4 text-jungle/20" />
                                Aucune commande pour le moment
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Quotes */}
                <Card className="mt-6 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="font-heading text-jungle flex items-center gap-2">
                            <FileText className="w-5 h-5 text-amber-600" />
                            Dernieres demandes de prix
                        </CardTitle>
                        <Button asChild variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50">
                            <Link to="/dashboard/devis">
                                Voir tout
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Link>
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {stats?.recent_quotes?.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-jungle/10">
                                        <TableHead className="text-jungle/70">N° Demande</TableHead>
                                        <TableHead className="text-jungle/70">Date</TableHead>
                                        <TableHead className="text-jungle/70 text-center">Produits</TableHead>
                                        <TableHead className="text-jungle/70">Statut</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {stats.recent_quotes.map((q) => {
                                        const qs = QUOTE_STATUS_MINI[q.status] || QUOTE_STATUS_MINI.pending;
                                        return (
                                            <TableRow key={q.id} className="border-jungle/10" data-testid={`quote-row-${q.id}`}>
                                                <TableCell className="font-mono text-sm font-medium text-jungle">{q.quote_number}</TableCell>
                                                <TableCell className="text-sm text-muted-foreground">{formatDateTime(q.created_at)}</TableCell>
                                                <TableCell className="text-center">
                                                    <Badge variant="outline" className="rounded-full border-jungle/20">{q.items?.length || 0}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={`${qs.color} rounded-full text-xs`}>{qs.label}</Badge>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="text-center py-6 text-muted-foreground text-sm">
                                Aucune demande de prix pour le moment
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
